import { c as _c } from "react/compiler-runtime";
import React, { useMemo, useState } from "react";
import { useLocalization } from "@/providers/useLocalization";
import { useTranslations } from "@/providers/TranslationsProvider";
import type { SpecialtyEntry } from "@/services/usersService";
import { termLabel } from "@/utils/profile";
import { Chip, FieldMessage, SEARCH, StepTitle, TILE_GRID } from "./fields";
import type { StepProps } from "./types";

// Mirrors the profile limits in psychometrist (settings.appointments.profile).
const MAX_TERMS = 30;
function toggle(list: string[], uuid: string): string[] {
  if (list.includes(uuid)) {
    return list.filter(u => u !== uuid);
  }
  return list.length >= MAX_TERMS ? list : [...list, uuid];
}
export default function SpecialtiesStep(t0) {
  const $ = _c(44);
  const {
    draft,
    update,
    errors,
    catalogue
  } = t0;
  const {
    t
  } = useTranslations();
  const {
    language
  } = useLocalization();
  const [query, setQuery] = useState("");
  let t1;
  let t2;
  if ($[0] !== catalogue || $[1] !== language || $[2] !== query) {
    const q = query.trim().toLocaleLowerCase(language);
    const visible = catalogue.filter(_temp).filter(entry_0 => !q || termLabel(entry_0, language).toLocaleLowerCase(language).includes(q)).sort(_temp2);
    t1 = visible.filter(_temp3);
    t2 = visible.filter(_temp4);
    $[0] = catalogue;
    $[1] = language;
    $[2] = query;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = [t1, t2];
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const [specialties, approaches] = t3;
  let t4;
  if ($[8] !== t) {
    t4 = t("join.specialties title", "Your specialties");
    $[8] = t;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t) {
    t5 = t("join.specialties subtitle", "Choose what you treat. Put your main focus first - patients see the first three on your card.");
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <StepTitle title={t4} subtitle={t5} />;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== catalogue.length || $[16] !== query || $[17] !== t) {
    t7 = catalogue.length > 12 && <div className="relative mb-5"><svg className="pointer-events-none absolute start-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-[#86868B]" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden={true}><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 10.5a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z" /></svg><input type="search" className={SEARCH} placeholder={t("join.search", "Search")} aria-label={t("join.search", "Search")} value={query} onChange={e_1 => setQuery(e_1.target.value)} /></div>;
    $[15] = catalogue.length;
    $[16] = query;
    $[17] = t;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t) {
    t8 = t("labels.specialties", "Specialties");
    $[19] = t;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t8) {
    t9 = <h3 className="mb-3 text-[15px] sm:text-[17px] tracking-[-0.022em] text-[var(--foreground)]">{t8}</h3>;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== draft || $[24] !== language || $[25] !== specialties || $[26] !== t || $[27] !== update) {
    t10 = specialties.length === 0 ? <p className="text-[14px] sm:text-[15px] text-[#86868B]">{t("join.no matches", "Nothing matches your search.")}</p> : <div className={TILE_GRID} role="group" aria-label={t("labels.specialties", "Specialties")}>{specialties.map(entry_1 => <Chip key={entry_1.uuid} tile={true} selected={draft.specialties.includes(entry_1.uuid)} badge={(draft.specialties.indexOf(entry_1.uuid) + 1).toLocaleString(language)} onClick={() => update({
        specialties: toggle(draft.specialties, entry_1.uuid)
      })}>{termLabel(entry_1, language)}</Chip>)}</div>;
    $[23] = draft;
    $[24] = language;
    $[25] = specialties;
    $[26] = t;
    $[27] = update;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  const t11 = errors["profile.specialties"];
  let t12;
  if ($[29] !== t11) {
    t12 = <FieldMessage error={t11} />;
    $[29] = t11;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== approaches || $[32] !== draft || $[33] !== language || $[34] !== t || $[35] !== update) {
    t13 = approaches.length > 0 && <><h3 className="mt-8 mb-3 text-[15px] sm:text-[17px] tracking-[-0.022em] text-[var(--foreground)]">{t("labels.approaches", "Therapy approaches")}<span className="text-[#86868B]"> ({t("join.optional", "Optional")})</span></h3><div className={TILE_GRID} role="group" aria-label={t("labels.approaches", "Therapy approaches")}>{approaches.map(entry_2 => <Chip key={entry_2.uuid} tile={true} selected={draft.approaches.includes(entry_2.uuid)} onClick={() => update({
          approaches: toggle(draft.approaches, entry_2.uuid)
        })}>{termLabel(entry_2, language)}</Chip>)}</div></>;
    $[31] = approaches;
    $[32] = draft;
    $[33] = language;
    $[34] = t;
    $[35] = update;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== t10 || $[38] !== t12 || $[39] !== t13 || $[40] !== t6 || $[41] !== t7 || $[42] !== t9) {
    t14 = <section>{t6}{t7}{t9}{t10}{t12}{t13}</section>;
    $[37] = t10;
    $[38] = t12;
    $[39] = t13;
    $[40] = t6;
    $[41] = t7;
    $[42] = t9;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  return t14;
}
function _temp4(e_0) {
  return e_0.kind === "approach";
}
function _temp3(e) {
  return e.kind === "specialty";
}
function _temp2(a, b) {
  return (a.sort_order ?? 0) - (b.sort_order ?? 0);
}
function _temp(entry) {
  return entry.active !== false;
}